import { Component } from "../../components/base/Component";
import { ComponentFactory } from "../../services/ComponentFactory";
import { ICfa } from "../../models/ICfa";
import { GiftCardService } from "./GiftCardService";
import { DataLayerService } from "../services/DataLayerService";

export class GiftCardForm extends Component {
	dxeBearerToken: string | undefined;
	form: HTMLFormElement;
	focused: boolean;

	constructor(_element: HTMLElement, _app: ICfa) {
		super(_element, _app);

		if (this._element.tagName !== "FORM") {
			throw new Error(`Non-form element passed in, element: ${this._element}`);
		}

		this.form = this._element as HTMLFormElement;
		this.focused = false;

		// tslint:disable-next-line
		this.initialize();
	}

	private async initialize(): Promise<void> {
		this.applyValidationRules(this.form);
		this.form.addEventListener("change", e => this.handleCustomEmailEffect());
		this.form.addEventListener("submit", e => this.handleSubmission(e));

		// confirm that user upload is accepted file type
		this.handleUserUpload();

		// CFAC-34725 Fire whenever a user starts filling out a form.
		// This event is generally fired after user input in the first form field
		this._element
			.querySelector("#fullName")
			?.addEventListener("focus", e => this.handleUserInput(e));

		// CFAC-34725 Fire whenever a user is presented with a form on a page.
		DataLayerService.pushDataLayerEvent_Forms("form_view", {
			identifier: this.layerIdentifier,
			name: this.layerName,
			type: "GiftCardForm"
		});
	}

	private get layerIdentifier(): string {
		return this._element.dataset.layerIdentifier || "";
	}

	private get layerName(): string {
		return this._element.dataset.layerName || "";
	}

	private handleUserUpload(): void {
		const uploadInput = this._element.querySelector("#image");
		const { acceptedFiles = "", customInputUploadMsg = "" } =
			this._element?.dataset;
		const acceptedFilesClean = acceptedFiles.split(",").map(i => i.trim());
		uploadInput?.addEventListener("change", e => {
			const ext = e.target?.value?.match(/\.([^\.]+)$/)[1];
			console.log(e);
			if (!acceptedFilesClean.includes(ext)) {
				const validator = $(this.form).validate();
				validator.showErrors({
					image: customInputUploadMsg
				});
				e.target?.value = "";
			} else {
				$(uploadInput).trigger("blur");
			}
		});
	}

	private handleCustomEmailEffect(): void {
		const customEmailDiv = this._element.querySelector(".email-verify-div");

		if (!customEmailDiv) {
			console.warn(
				"No custom email div found. Check html and selector: .email-verify-div"
			);
			return;
		}

		const customEmailCheckbox = this._element.querySelector(
			"input[value='customEmailOption']"
		);

		if (customEmailCheckbox?.checked) {
			$(customEmailDiv).slideDown();
		} else {
			$(customEmailDiv).slideUp();
		}
	}

	private applyValidationRules(form: HTMLFormElement): void {
		const messages = this._element.dataset;
		const errorClass = "hasError";

		$(form).validate({
			errorClass: "hasError",
			errorPlacement: (error, element) => {
				error.appendTo(element.next(".error-container"));
			},
			highlight: element => {
				$(element).parent().addClass(errorClass);
			},
			messages: {
				fullName: {
					required: messages.customNameMsg ?? ""
				},
				email: messages.customEmailMsg ?? "",
				address: messages.customAddressMsg ?? "",
				reason: messages.customReplaceReasonMsg ?? "",
				acknowledge: messages.customAcknowledgeMsg ?? "",
				customEmail: messages.customEmailVerifyMsg ?? ""
			},
			rules: {
				fullName: {
					required: true
				},
				email: {
					email: true,
					required: true
				},
				address: {
					required: true
				},
				reason: {
					required: true
				},
				acknowledge: {
					required: true
				},
				customEmail: {
					email: true,
					required: "#customEmailOption:checked"
				}
			},
			unhighlight: element => {
				$(element).parent().removeClass(errorClass);
			}
		});
	}

	// CFAC-34725 Fire whenever a user starts filling out a form.
	// This event is generally fired after user input in the first form field
	private async handleUserInput(e: Event): Promise<void> {
		if (!this.focused) {
			DataLayerService.pushDataLayerEvent_Forms("form_start", {
				identifier: this.layerIdentifier,
				name: this.layerName,
				type: "GiftCardForm"
			});

			this.focused = true;
		}
	}

	private async handleSubmission(e: Event): Promise<void> {
		e.preventDefault();
		this.toggleSubmitBtn(false);
		const formHasErrors = !this.validate();
		if (formHasErrors) {
			this.toggleSubmitBtn(true);

			// CFAC-34725 Fire whenever a user unsuccessfully completes a form
			DataLayerService.pushDataLayerEvent_Forms("form_error", {
				error_category: "GiftCardFormSubmission",
				error_message: "Form Has Errors",
				identifier: this.layerIdentifier,
				name: this.layerName,
				type: "GiftCardForm"
			});

			return;
		}

		await this.submitForm();
	}

	private async submitForm(): Promise<void | null> {
		const {
			recaptchaEndpoint = "",
			submissionEndpoint = "",
			formDatasourceId = "",
			redirectSuccess,
			redirectError
		} = this._element.dataset;

		let formData = new FormData(this.form);
		formData.append("formDatasourceId", formDatasourceId);
		const recaptchaVal = this.form["g-recaptcha-response"]?.value;

		if (!recaptchaVal) {
			console.warn(
				`No recaptcha value found in payload`
			);
			this.toggleRecaptchaErr(true);
			this.toggleSubmitBtn(true);

			return null;
		}

		const promise = $.ajax({
			contentType: "application/json",
			data: JSON.stringify({
				recaptchaGuid: recaptchaVal
			}),
			type: "POST",
			url: recaptchaEndpoint
		});

		const recaptchaResult = await Promise.resolve(promise);

		if (!recaptchaResult) {
			console.warn("Recaptcha failed.");
			this.toggleRecaptchaErr(true);
			this.toggleSubmitBtn(true);

			return;
		}

		const [result, err] = await GiftCardService.sendForm(
			submissionEndpoint,
			formData
		);

		if (err) {
			// CFAC-34725 Fire whenever a user unsuccessfully completes a form
			DataLayerService.pushDataLayerEvent_Forms("form_error", {
				error_category: "GiftCardFormSubmission",
				error_message: "Error in submitting form.",
				identifier: this.layerIdentifier,
				name: this.layerName,
				type: "GiftCardForm"
			});

			console.warn("Error in submitting form.");
			window.location.href = redirectError;

			return;
		}

		if (result && result.success) {
			// CFAC-34725 Fire whenever a user successfully completes a form
			DataLayerService.pushDataLayerEvent_Forms("form_complete", {
				identifier: this.layerIdentifier,
				name: this.layerName,
				type: "GiftCardForm"
			});

			window.location.href = redirectSuccess;
		} else {
			// Account for false response from BE:
			window.location.href = redirectError;
		}
	}

	private toggleRecaptchaErr(toggleOn: boolean): void {
		const recaptchaField = this._element.querySelector(
			"[data-element='recaptchaField']"
		);

		if (!recaptchaField) {
			console.warn("No recaptcha field found. Check DOM.");

			return;
		}

		toggleOn
			? recaptchaField.classList.add("display")
			: recaptchaField.classList.remove("display");
	}

	private validate(): boolean {
		if (this._element) {
			return $(this._element).valid();
		}

		return false;
	}

	private toggleSubmitBtn(toggleOn: boolean): void {
		const submitBtn = this._element.querySelector("button[type='submit']");
		const disabledClass = "disabled";

		if (!submitBtn) {
			throw new Error(`Submit button not found for ${submitBtn}`);
		}

		toggleOn
			? submitBtn.classList.remove(disabledClass)
			: submitBtn.classList.add(disabledClass);
	}
}

ComponentFactory.registerComponent("giftCardForm", GiftCardForm);
